// components/ui — 시안 F(Notion) 공용 부품(#1867 f7).
// 이 잡에서는 만들기만 한다 — 화면 적용은 메뉴별 잡(f11~)이 한다.
// · 색은 var(--gw-…, <라이트 hex>) — 다크 전환(f9)이 CSS 변수만 바꾸면 따라온다. TONES 색만 hex 그대로.
// · hover·포커스·구간(@media) 규칙은 인라인 style 로 못 해서 public/index.html 의 .gw-* 클래스가 맡는다.
// · 아이콘은 @tabler/icons-react 요소(<IconX />)나 참조(IconX)를 props 로 받는다 — renderIcon 한 곳에서만 그린다.
// · fontFamily 는 넣지 않는다(폼 글꼴은 index.html 이 상속시킨다 · backend/tests/test_ui_fonts.py).
import { cloneElement, createElement, isValidElement, useRef } from 'react';
import { IconSearch } from '@tabler/icons-react';

import { COLORS, ICON_STROKE, RADIUS, TONES, badge, btn, card } from '../../utils/uiStyles';

const isDev = process.env.NODE_ENV !== 'production';

const V = {
  text: `var(--gw-text, ${COLORS.text})`,
  secondary: `var(--gw-secondary, ${COLORS.textSub})`,
  muted: `var(--gw-muted, ${COLORS.textMuted})`,
  line: `var(--gw-line, ${COLORS.borderSoft})`,
  border: `var(--gw-border, ${COLORS.border})`,
  surface: `var(--gw-surface, ${COLORS.surface})`,
  graySoft: 'var(--gw-gray-soft, #f1f1ef)',
};

const toneOf = (tone) => (tone && Object.prototype.hasOwnProperty.call(TONES, tone) ? TONES[tone] : null);
const joinClass = (base, extra) => [base, extra].filter(Boolean).join(' ');
const isComponentRef = (icon) => typeof icon === 'function' || (typeof icon === 'object' && Boolean(icon.$$typeof));

// 아이콘 그리기 — 판정 순서를 바꾸지 않는다.
// React 요소도 $$typeof 를 가진 객체라, 참조(④)를 요소(②)보다 먼저 보면 요소가 createElement 로 가서 화면이 죽는다.
function renderIcon(icon, size) {
  if (!icon) return null;                                                     // ①
  if (isValidElement(icon)) {                                                 // ② 호출부가 준 size·stroke 는 덮지 않는다
    return cloneElement(icon, { size: icon.props.size ?? size, stroke: icon.props.stroke ?? ICON_STROKE });
  }
  if (typeof icon === 'string' || typeof icon === 'number') return icon;      // ③ 글자 아이콘(이모지)
  if (isComponentRef(icon)) return createElement(icon, { size, stroke: ICON_STROKE }); // ④ tabler 참조(forwardRef 객체)
  return null;                                                                // ⑤
}

// 페이지 머리 아이콘 — 크기·선 굵기는 index.html 의 .gw-ph-icon svg 가 맡으므로 요소를 복제하지 않는다.
function renderHeaderIcon(icon) {
  if (!icon) return null;
  if (isValidElement(icon)) return icon;
  if (typeof icon === 'string' || typeof icon === 'number') return icon;
  if (isComponentRef(icon)) return createElement(icon);
  return null;
}

const emptyText = { textAlign: 'center', padding: '36px 16px', fontSize: '13px', color: V.muted };

// 본문 틀 — .gw-page(최대 1440 · 구간별 패딩은 index.html)
export function Page({ children, maxWidth, className, style }) {
  return (
    <div className={joinClass('gw-page', className)} style={{ ...(maxWidth != null ? { maxWidth } : {}), ...style }}>
      {children}
    </div>
  );
}

// 페이지 머리 — size 'lg'(원 70) | 'md'(채팅 · 원 60). tone 은 TONES 키(모르면 회색).
export function PageHeader({ icon, tone, size = 'lg', title, subtitle, date, actions }) {
  const iconNode = renderHeaderIcon(icon);
  const hasIcon = iconNode !== null;
  const t = toneOf(tone);
  const rowClass = 'gw-ph' + (size === 'md' ? ' gw-ph--md' : '') + (hasIcon ? '' : ' gw-ph--noicon');
  return (
    <>
      {date && <div className="gw-ph-date">{date}</div>}
      <div className={rowClass}>
        {hasIcon && (
          <div
            className="gw-ph-icon"
            aria-hidden="true"
            style={{ background: t ? t.soft : V.graySoft, color: t ? t.ink : V.text }}
          >
            {iconNode}
          </div>
        )}
        <div>
          <h1 className="gw-h1">{title}</h1>
          {subtitle && <p className="gw-ph-sub">{subtitle}</p>}
        </div>
        {actions && <div className="gw-ph-actions">{actions}</div>}
      </div>
    </>
  );
}

/**
 * 알약 탭 — role="tablist". ←/→(끝에서 순환)·Home/End 는 포커스만 옮기고, 선택은 클릭·Enter·Space(단추 기본 click)로 한다.
 * idBase 필수 — 탭 id = `${idBase}-tab-${key}`, aria-controls = `${idBase}-panel-${key}`.
 * 화면 쪽은 <div role="tabpanel" id={`${idBase}-panel-${key}`} aria-labelledby={`${idBase}-tab-${key}`}> 를 그린다.
 * 탭 이름 = aria-label={label}(건수가 이름에 섞이지 않게) · count 는 `${idBase}-count-${key}` 로 aria-describedby(정보라 aria-hidden 금지).
 */
export function PillTabs({ items = [], value, onChange, ariaLabel, idBase }) {
  const refs = useRef([]);
  if (!idBase && isDev) console.error('PillTabs: idBase 필수');
  const selectedIndex = items.findIndex((item) => item.key === value);
  const focusIndex = selectedIndex >= 0 ? selectedIndex : 0;

  const onKeyDown = (event, index) => {
    const last = items.length - 1;
    let next = null;
    if (event.key === 'ArrowRight') next = index >= last ? 0 : index + 1;
    else if (event.key === 'ArrowLeft') next = index <= 0 ? last : index - 1;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = last;
    if (next === null) return;
    event.preventDefault();
    refs.current[next]?.focus();
  };

  return (
    <div role="tablist" aria-label={ariaLabel} className="gw-tabs">
      {items.map((item, index) => {
        const selected = index === selectedIndex;
        return (
          <button
            key={item.key}
            ref={(el) => { refs.current[index] = el; }}
            type="button"
            role="tab"
            className="gw-tab"
            id={`${idBase}-tab-${item.key}`}
            aria-controls={`${idBase}-panel-${item.key}`}
            aria-label={item.label}
            aria-describedby={item.count != null ? `${idBase}-count-${item.key}` : undefined}
            aria-selected={selected}
            tabIndex={index === focusIndex ? 0 : -1}
            onClick={() => onChange?.(item.key)}
            onKeyDown={(event) => onKeyDown(event, index)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '5px 10px', borderRadius: '18px',
              fontSize: '13px', border: 0, cursor: 'pointer', whiteSpace: 'nowrap',
              background: selected ? V.graySoft : 'transparent',
              color: selected ? V.text : V.muted,
              fontWeight: selected ? 500 : 400,
            }}
          >
            {renderIcon(item.icon, 16)}
            {item.label}
            {item.count != null && <span id={`${idBase}-count-${item.key}`} style={{ fontSize: '12px', color: V.muted }}>{item.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

// 섹션 제목 — h2 와 count·action 은 형제(제목의 접근성 이름에 단추 글자가 섞이지 않게).
export function SectionTitle({ icon, title, count, action }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '7px', margin: '0 0 14px' }}>
      <h2 style={{ margin: 0, fontSize: '14px', fontWeight: 620, display: 'flex', alignItems: 'center', gap: '7px', color: V.text }}>
        {icon && <span aria-hidden="true" style={{ display: 'inline-flex', color: V.muted, fontSize: '16px' }}>{renderIcon(icon, 16)}</span>}
        {title}
      </h2>
      {count != null && <span style={{ fontSize: '12px', fontWeight: 400, color: V.muted, marginLeft: 'auto' }}>{count}</span>}
      {action && <div style={{ marginLeft: count != null ? 0 : 'auto' }}>{action}</div>}
    </div>
  );
}

// 단추 — variant 는 uiStyles btn() 과 같다. hover 는 index.html 의 .gw-btn--<variant>(danger 는 hover 규칙 없음).
export function Button({ variant = 'primary', size = 'md', icon, type = 'button', className, style, children, ...rest }) {
  return (
    <button
      {...rest}
      type={type}
      className={joinClass(`gw-btn gw-btn--${variant}`, className)}
      style={{
        ...btn(variant, size),
        ...(icon ? { display: 'inline-flex', alignItems: 'center', gap: '6px' } : {}),
        ...style,
      }}
    >
      {renderIcon(icon, 15)}
      {children}
    </button>
  );
}

// 아이콘 단추 — label 필수(aria-label·title). 아이콘 18.
export function IconButton({ icon, label, className, style, ...rest }) {
  if (!label && isDev) console.error('IconButton: label 필수');
  return (
    <Button
      {...rest}
      variant="icon"
      icon={renderIcon(icon, 18)}
      aria-label={label}
      title={label}
      className={className}
      style={style}
    />
  );
}

// 검색 상자 — onChange 는 이벤트를 넘긴다(값은 event.target.value).
export function SearchInput({ value, onChange, placeholder, ariaLabel, width }) {
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: '7px', height: '30px', padding: '0 9px', boxSizing: 'border-box',
        border: `1px solid ${V.border}`, borderRadius: RADIUS.md, background: V.surface,
        ...(width != null ? { width } : {}),
      }}
    >
      <IconSearch size={14} stroke={ICON_STROKE} aria-hidden="true" style={{ color: V.muted, flexShrink: 0 }} />
      <input
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        aria-label={ariaLabel || placeholder}
        style={{
          border: 0, background: 'transparent', flex: 1, minWidth: 0, width: '100%',
          fontSize: '12px', color: V.text,
        }}
      />
    </div>
  );
}

// 알약 — 앞 7px 점(글자색) + 12px 글자 · 굵기 400(시안 .pill)
export function Pill({ tone = 'gray', dot = true, children }) {
  return (
    <span style={{ ...badge(tone), fontWeight: 400 }}>
      {dot && (
        <span
          className="gw-pill-dot"
          aria-hidden="true"
          style={{ width: '7px', height: '7px', borderRadius: '50%', background: 'currentColor', flexShrink: 0 }}
        />
      )}
      {children}
    </span>
  );
}

// 태그 — 패딩 2 6 · r3 · 12px(시안 .tag)
export function Tag({ tone = 'gray', children }) {
  const t = toneOf(tone) || TONES.gray;
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 6px', lineHeight: '17px',
        borderRadius: RADIUS.xs, fontSize: '12px', background: t.soft, color: t.ink,
      }}
    >
      {children}
    </span>
  );
}

// 지표 칸 묶음 — 4열(≤600 2열 · index.html)
export function MetricGroup({ children }) {
  return <div className="gw-metrics">{children}</div>;
}

export function MetricTile({ icon, label, value, unit, foot }) {
  return (
    <div className="gw-metric">
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: V.secondary }}>
        {label}
        {icon && <span aria-hidden="true" style={{ display: 'inline-flex', color: V.muted }}>{renderIcon(icon, 15)}</span>}
      </div>
      <div className="gw-metric-value">
        {value}
        {unit && <span style={{ fontSize: '12px', fontWeight: 400, color: V.muted, marginLeft: '5px', letterSpacing: 0 }}>{unit}</span>}
      </div>
      {foot && <div style={{ fontSize: '10px', color: V.muted }}>{foot}</div>}
    </div>
  );
}

// 표 셀 — 열 폭은 th style.width 로만(<colgroup> 금지 — 모바일 stack 모드에서 블록 행이 첫 열 폭으로 줄어든다)
const cellBase = {
  borderBottom: `1px solid ${V.line}`, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
};
const ROW_IGNORE = 'button,a,input,select,textarea,[role=button]';

/**
 * 데이터 표(시안 .database) — columns [{ key, label, icon?, width?, render?: (row, rowIndex) => node }].
 * rowKey = 키 이름 문자열 또는 (row) => key 함수(없으면 행 번호).
 * onRowClick 이 있으면 행을 클릭·Enter 로 연다 — 행 안 단추·링크·입력에서 난 이벤트는 무시한다.
 */
export function DataTable({
  columns = [], rows = [], rowKey, onRowClick, empty = '표시할 항목이 없습니다.', footer, minWidth, stackOnMobile = false,
}) {
  const keyOf = (row, index) => {
    if (typeof rowKey === 'function') return rowKey(row);
    if (typeof rowKey === 'string' && row && row[rowKey] != null) return row[rowKey];
    return index;
  };
  const fromInner = (event) => {
    const hit = event.target && typeof event.target.closest === 'function' ? event.target.closest(ROW_IGNORE) : null;
    return Boolean(hit && hit !== event.currentTarget && event.currentTarget.contains(hit));
  };

  return (
    <>
      <div style={{ overflowX: 'auto' }}>
        <table
          className={stackOnMobile ? 'gw-table--stack' : undefined}
          style={{
            width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'left',
            ...(minWidth != null ? { minWidth } : {}),
          }}
        >
          <thead>
            <tr>
              {columns.map((col, ci) => (
                <th
                  key={col.key}
                  style={{
                    ...cellBase, height: '33px', padding: ci === 0 ? '5px 9px 5px 4px' : '5px 9px',
                    fontSize: '12px', fontWeight: 400, color: V.muted,
                    ...(col.width != null ? { width: col.width } : {}),
                  }}
                >
                  {col.icon && (
                    <span aria-hidden="true" style={{ display: 'inline-flex', verticalAlign: 'middle', marginRight: '5px' }}>
                      {renderIcon(col.icon, 14)}
                    </span>
                  )}
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td className="gw-empty" colSpan={Math.max(columns.length, 1)} style={emptyText}>{empty}</td>
              </tr>
            ) : rows.map((row, ri) => (
              <tr
                key={keyOf(row, ri)}
                className={onRowClick ? 'gw-tr gw-tr--click' : 'gw-tr'}
                tabIndex={onRowClick ? 0 : undefined}
                onClick={onRowClick ? (event) => { if (!fromInner(event)) onRowClick(row); } : undefined}
                onKeyDown={onRowClick ? (event) => {
                  if (event.key === 'Enter' && !fromInner(event)) onRowClick(row);
                } : undefined}
              >
                {columns.map((col, ci) => (
                  <td
                    key={col.key}
                    style={{
                      ...cellBase, height: '41px', padding: ci === 0 ? '7px 9px 7px 4px' : '7px 9px',
                      color: ci === 0 ? V.text : V.secondary,
                    }}
                  >
                    {col.render ? col.render(row, ri) : row[col.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {footer && <div style={{ fontSize: '11px', color: V.muted, padding: '16px 0' }}>{footer}</div>}
    </>
  );
}

// 빈 화면(시안 .empty) — icon·title·action 은 시안에 없는 확장(화면이 고를 때만)
export function EmptyState({ icon, title, description, action }) {
  return (
    <div style={emptyText}>
      {icon && (
        <div aria-hidden="true" style={{ display: 'flex', justifyContent: 'center', marginBottom: '8px', color: V.muted }}>
          {renderIcon(icon, 28)}
        </div>
      )}
      {title && <div style={{ fontSize: '14px', fontWeight: 600, color: V.text }}>{title}</div>}
      {description && <div style={{ marginTop: title ? '4px' : 0 }}>{description}</div>}
      {action && <div style={{ marginTop: '12px' }}>{action}</div>}
    </div>
  );
}

export function LoadingState({ children = '불러오는 중…' }) {
  return <div role="status" style={emptyText}>{children}</div>;
}

export function ErrorState({ message, onRetry }) {
  return (
    <div
      role="alert"
      style={{
        display: 'flex', alignItems: 'center', gap: '12px', borderRadius: RADIUS.lg, padding: '12px 14px',
        background: TONES.red.soft, color: TONES.red.ink, fontSize: '13px',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>{message}</div>
      {onRetry && <Button variant="secondary" size="sm" onClick={() => onRetry()}>다시 시도</Button>}
    </div>
  );
}

// 아바타 — 이름 첫 글자. 글자 크기 = size ≥31 → 12px · ≥25 → 10px · 그 밖 9px.
export function Avatar({ name, size = 28, shape = 'circle' }) {
  const first = Array.from(String(name ?? '').trim())[0] || '';
  const fontSize = size >= 31 ? '12px' : size >= 25 ? '10px' : '9px';
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        width: size, height: size, borderRadius: shape === 'square' ? '7px' : '50%',
        background: V.graySoft, color: V.secondary, fontSize, fontWeight: 600, lineHeight: 1,
      }}
    >
      {first}
    </span>
  );
}

// 카드 — uiStyles card 그대로
export function Card({ children, padding = '16px 18px', className, style }) {
  return <div className={joinClass('gw-card', className)} style={{ ...card, padding, ...style }}>{children}</div>;
}
